/**
 * Credential recovery (operator) — the tenant's recovery requests and a person's recovery passes.
 *
 * Every path is tenant-addressed (/api/v1/tenants/{tenant_id}/credential-recovery/…). On the
 * platform console that tree binds the tenant from the path after the caller's own permission; on
 * a tenant administration host the api-client rewrites it to /api/tenant/v1/credential-recovery/…,
 * which resolves the same tenant from the session. The session-tenant /api/v1/credential-recovery
 * tree is never used here: for a platform administrator it resolves the infrastructure tenant, not
 * the customer the page is about.
 *
 * Reads need credential_recovery.read. Revoking a pass needs credential_recovery.manage and a fresh
 * step-up, driven by withStepUp. The console does not mint passes: recovery is decided through the
 * request approval plan in the governance queue.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch, type ApiError } from "@/lib/api-client";
import { withStepUp } from "@/lib/with-step-up";
import { recoveryPassesResponseSchema, recoveryRequestSchema, recoveryRequestsPageSchema } from "./api.generated";
import type { RecoveryPass, RecoveryRequest, RecoveryRequestsPage, RecoveryRequestsSearch } from "./schemas";

function base(tenantID: string): string {
  return `/api/v1/tenants/${encodeURIComponent(tenantID)}/credential-recovery`;
}

export const credentialRecoveryKeys = {
  all: (tenantID: string) => ["credential-recovery", tenantID] as const,
  requests: (tenantID: string, search: RecoveryRequestsSearch) =>
    ["credential-recovery", tenantID, "requests", search] as const,
  request: (tenantID: string, requestID: string) => ["credential-recovery", tenantID, "request", requestID] as const,
  passes: (tenantID: string, userId: string) => ["credential-recovery", tenantID, "passes", userId] as const,
};

export async function fetchRecoveryRequests(tenantID: string, search: RecoveryRequestsSearch): Promise<RecoveryRequestsPage> {
  const params = new URLSearchParams();
  if (search.status) params.set("status", search.status);
  if (search.cursor) params.set("cursor", search.cursor);
  const query = params.toString();
  const raw = await apiFetch<unknown>(`${base(tenantID)}/requests${query ? `?${query}` : ""}`);
  return recoveryRequestsPageSchema.parse(raw);
}

export async function fetchRecoveryRequest(tenantID: string, requestID: string): Promise<RecoveryRequest> {
  const raw = await apiFetch<unknown>(`${base(tenantID)}/requests/${encodeURIComponent(requestID)}`);
  return recoveryRequestSchema.parse(raw);
}

export async function fetchRecoveryPasses(tenantID: string, userId: string): Promise<RecoveryPass[]> {
  const raw = await apiFetch<unknown>(`${base(tenantID)}/users/${encodeURIComponent(userId)}/recovery-passes`);
  return recoveryPassesResponseSchema.parse(raw).items;
}

export function useRecoveryRequestsQuery(tenantID: string, search: RecoveryRequestsSearch) {
  return useQuery<RecoveryRequestsPage, ApiError>({
    queryKey: credentialRecoveryKeys.requests(tenantID, search),
    queryFn: () => fetchRecoveryRequests(tenantID, search),
    enabled: tenantID.length > 0,
  });
}

export function useRecoveryRequestQuery(tenantID: string, requestID: string) {
  return useQuery<RecoveryRequest, ApiError>({
    queryKey: credentialRecoveryKeys.request(tenantID, requestID),
    queryFn: () => fetchRecoveryRequest(tenantID, requestID),
    enabled: tenantID.length > 0 && requestID.length > 0,
  });
}

export function useRecoveryPassesQuery(tenantID: string, userId: string) {
  return useQuery<RecoveryPass[], ApiError>({
    queryKey: credentialRecoveryKeys.passes(tenantID, userId),
    queryFn: () => fetchRecoveryPasses(tenantID, userId),
    enabled: tenantID.length > 0 && userId.length > 0,
  });
}

/** Revoke one of the person's active passes, then refresh their pass list. */
export function useRevokeRecoveryPassMutation(tenantID: string, userId: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, { passId: string }>({
    mutationFn: ({ passId }) =>
      withStepUp(async () => {
        await apiFetch<unknown>(
          `${base(tenantID)}/users/${encodeURIComponent(userId)}/recovery-pass/${encodeURIComponent(passId)}`,
          { method: "DELETE" },
        );
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: credentialRecoveryKeys.passes(tenantID, userId) }),
  });
}
